import React, { useState, useEffect } from 'react';
import { ClientProvider } from './context/ClientContext';
import Login from './components/shared/Login';
import DashboardLayout from './layouts/DashboardLayout';
import ComingSoon from './components/shared/ComingSoon';
import NotFound from './components/shared/NotFound';
import RootPage from './components/shared/RootPage';

export default function App() {
  const slug = window.location.pathname.split('/').filter(Boolean)[0] || null;

  const [clientConfig, setClientConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authenticated, setAuthenticated] = useState(null);

  // Load client config from static JSON
  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    Promise.all([
      fetch(`/clients/${slug}/brand.json`).then(r => r.ok ? r.json() : null),
      fetch(`/clients/${slug}/data-sources.json`).then(r => r.ok ? r.json() : null),
    ])
      .then(([brand, dataSources]) => {
        if (!brand || !dataSources) {
          setError('not-found');
        } else {
          setClientConfig({ slug, brand, dataSources });

          // Load client font if specified
          if (brand.typography?.fontCDN) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = brand.typography.fontCDN;
            document.head.appendChild(link);
          }

          document.title = `${brand.brandName} - Google Ads Dashboard`;
        }
      })
      .catch(() => setError('not-found'))
      .finally(() => setLoading(false));
  }, [slug]);

  // No slug = root page
  if (!slug) return <RootPage />;

  // Loading config
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-400" />
      </div>
    );
  }

  // Unknown client
  if (error === 'not-found') return <NotFound />;

  const hasCustomDashboard = clientConfig.dataSources.dashboardReady !== false;

  return (
    <ClientProvider value={clientConfig}>
      <AuthGate
        slug={slug}
        authenticated={authenticated}
        setAuthenticated={setAuthenticated}
      >
        {hasCustomDashboard ? <DashboardLayout /> : <ComingSoon />}
      </AuthGate>
    </ClientProvider>
  );
}

function AuthGate({ slug, authenticated, setAuthenticated, children }) {
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    fetch(`/api/auth?client=${slug}`)
      .then(res => {
        if (res.ok) return setAuthenticated(true);
        if (res.status >= 500) return setAuthenticated(true); // dev fallback — API not running
        setAuthenticated(false);
      })
      .catch(() => {
        setAuthenticated(true); // dev fallback
      })
      .finally(() => setChecking(false));
  }, [slug]);

  if (checking) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-400" />
      </div>
    );
  }

  if (!authenticated) {
    return <Login onSuccess={() => setAuthenticated(true)} />;
  }

  return children;
}
